/**
 * ELIO Board USB packet codec — packet_t (STX/ETX/ESC/CRC) 와이어 호환.
 * 정본: elio-board-firmware/USBDongle/Dongle_App/Module/usb_packet.h
 * 분석: elio-board-firmware/USBDongle/docs/PROTOCOL_ANALYSIS.md §1
 */

const STX = 0x02;
const ETX = 0x03;
const ESC = 0x23;
const DLE = 0x40;
const CRC_SEED = 0xFF;

const CMD_EXECUTE = 0x01;
const CMD_STATUS = 0x02;
const CMD_SENSOR_CFG = 0xF0;
const CMD_ENV = 0xB2;
const UDP = 0x30;

/**
 * EXECUTE 출력 버퍼 인덱스 매핑. 펌웨어 app_usb_power2_t 와이어 정합.
 * @readonly
 * @enum {number}
 */
const IDX = {
    UDP: 0,
    CMD: 1,
    DC1: 2,
    DC2: 3,
    SV1: 4,
    SV2: 5,
    V3: 6,
    V5: 7,
    IO1: 8,
    IO2: 9,
    IO3: 10,
    IO4: 11,
    SONIC: 12,
    LINE1: 13,
    LINE2: 14
};

/**
 * 새 EXECUTE 출력 버퍼 인스턴스 (15B 0-init, header 채움).
 * @returns {Uint8Array} 새 출력 버퍼
 */
const makeOutputBuffer = function () {
    return new Uint8Array([UDP, CMD_EXECUTE, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]);
};

/**
 * 단일 바이트 ESC 인코딩.
 * 0x00–0x03 → ESC + (b ^ DLE), ESC 자체 → ESC ESC.
 * @param {number} ch byte
 * @param {Array<number>} s 출력 stream
 */
const _encodeByte = function (ch, s) {
    if (ch <= ETX) {
        s.push(ESC);
        s.push((ch ^ DLE) & 0xFF);
    } else if (ch === ESC) {
        s.push(ESC);
        s.push(ESC);
    } else {
        s.push(ch);
    }
};

/**
 * 페이로드를 STX/ETX/CRC 프레이밍으로 인코딩.
 * @param {Array<number>|Uint8Array} arr 페이로드 (header 포함)
 * @returns {Uint8Array} 와이어 바이트
 */
const encodePacket = function (arr) {
    const s = [STX];
    let crc = CRC_SEED;
    for (const ch of arr) {
        const b = ch & 0xFF;
        _encodeByte(b, s);
        crc ^= b;
    }
    _encodeByte(crc, s);
    s.push(ETX);
    return new Uint8Array(s);
};

/**
 * 스트리밍 ElioPacket 디코더.
 * STX 받으면 reset, ETX 시 CRC 검증 후 콜백.
 */
class ElioParser {
    constructor (onPacket) {
        this._cb = onPacket;
        this._reset();
    }

    _reset () {
        this._state = 0;
        this._buf = [];
    }

    addData (data) {
        for (const b of data) {
            this._byte(b & 0xFF);
        }
    }

    _byte (b) {
        if (b === STX) {
            this._reset();
            this._state = 1;
            return;
        }
        if (this._state === 0) {
            return;
        }
        if (this._state === 1) {
            if (b === ETX) {
                this._finalize();
            } else if (b === ESC) {
                this._state = 2;
            } else if (b < ETX) {
                this._reset();
            } else {
                this._buf.push(b);
            }
        } else {
            this._buf.push(b === ESC ? ESC : (b ^ DLE));
            this._state = 1;
        }
    }

    _finalize () {
        if (this._buf.length < 2) {
            this._reset();
            return;
        }
        const rx = this._buf.pop();
        let c = CRC_SEED;
        for (const x of this._buf) {
            c ^= x;
        }
        if (c === rx) {
            this._cb(new Uint8Array(this._buf));
        }
        this._reset();
    }
}

module.exports = {
    encodePacket,
    ElioParser,
    makeOutputBuffer,
    IDX,
    CMD_SENSOR_CFG,
    CMD_ENV,
    CMD_STATUS,
    UDP
};
